<template>
  <div class="container">
    <div class="row padding-after-navbar">
      <div class="col text-left">
        <h1 class="mb-3">Customize Your Designs</h1>
        <div class="card card-body shadow">
          <p>Story Synth offers many ways for you to customize your design:</p>
          <ul>
            <li>Formats: several distinct game structures for different types of play</li>
            <li>Extensions: additional functionality to support your game</li>
            <li>Custom Options: ways to tweak the formats to suit your vision</li>
          </ul>
          <p>For detailed information on all of the above, visit the <a
              href="https://docs.storysynth.org/guide/formats.html" target="_blank">Story Synth Documentation</a>. If
            you have a request for a new format, extension, or custom option, send an email to <a
              href="mailto:suggestions@storysynth.org">suggestions@storysynth.org</a>.</p>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col">
        <h2>Story Synth Formats</h2>
      </div>
    </div>

    <div class="row">
      <div class="col">
        <div class="card card-body shadow">
          <h3>Shuffled</h3>
          <img src="/img/ikara-thumbnail.png" class="rounded mx-auto d-block format-thumbnail"
            alt="thumbnail of the shuffled format">
          <div>
            <p class="format-line">In this format, players draw a series of card prompts from one or more decks. The
              order of cards for the first deck is always the same, which makes it a fit for instructions. Subsequent
              decks are randomized within each deck (e.g. you'll randomly draw through all of deck B before moving to
              deck C).</p>
            <p class="format-line">Inspired by <a href="https://www.evilhat.com/home/for-the-queen/">For the Queen</a>
              and <a href="https://buriedwithoutceremony.com/the-quiet-year">The Quiet Year</a>.</p>
            <p class="format-line">Templates: <a
                href="https://docs.google.com/spreadsheets/d/1N5eeyKTVWo5QeGcUV_zYtwtR0DikJCcvcj6w69UkC1w/edit?usp=sharing">Simple
                Deck</a>, <a
                href="https://docs.google.com/spreadsheets/d/1N2N8URSswhmxLalPOkzi5d4kdeFiGrsobEmM_ojk0ow/edit?usp=sharing">Multi-deck</a>
            </p>
            <p class="format-line">Example Games: <a href="https://storysynth.org/Games/Clash-At-Ikara/">Clash at
                Ikara</a> and <a href="https://storysynth.org/Games/Raised-By-Mechs/">Raised by Mechs</a></p>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col">
        <div class="card card-body shadow">
          <h3>Generator</h3>
          <img src="/img/generator-thumbnail.png" class="rounded mx-auto d-block format-thumbnail"
            alt="thumbnail of the generator format">
          <p>This format shows randomly selected options from different categories over a customized layout of rows and
            columns. Players can regenerate (i.e. reroll) the entire page of selections or just individual categories.
          </p>
          <p>Template: <a
              href="https://docs.google.com/spreadsheets/d/1F0g3rVHxRA7O0rRMIQSsLCepJStxBO6pa7QJUNJb3K0/edit?usp=sharing">Generator
              Template</a></p>
          <p>Example Games: <a href="https://storysynth.org/Games/Aethelreds-Academy">Aethelred's Academy</a> and <a
              href="https://storysynth.org/Games/Umberdred-Institute/">Umberdred Institute</a></p>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col">
        <div class="card card-body shadow">
          <h3>Hexmap</h3>
          <img src="https://diegeticgames.com/uploads/screenshot-2022-12-31-at-8-46-13-am.png" class="rounded mx-auto d-block format-thumbnail"
            alt="thumbnail of the hexmap format">
          <p>This format features a map made of hexagons, each containg content relevant to the players and / or faciliator. Maps can be fixed or randomly generated.</p>
          <p>Template: <a
              href="https://docs.google.com/spreadsheets/d/1tW0XdThjJesSdRcU_Tj1hk-V008QIpVw87UDcTdGQ-k/edit#gid=0">Hexmap
              Template</a></p>
          <p>Example Game: <a
              href="https://storysynth.org/Hexmap/1tW0XdThjJesSdRcU_Tj1hk-V008QIpVw87UDcTdGQ-k/">Hexmap Demo</a>
          </p>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col">
        <div class="card card-body shadow">
          <h3>Hexflower</h3>
          <img src="/img/hexflower-thumbnail.png" class="rounded mx-auto d-block format-thumbnail"
            alt="thumbnail of the hexflower format">
          <p>This format features a big hexagon made of smaller hexagons, with each hex having it's own content.
            Players can click to select different hexes or roll to randomly move among them.</p>
          <p>Inspired by the <a href="https://goblinshenchman.wordpress.com/hex-power-flower/">Hexflower Cookbook</a>
          </p>
          <p>Template: <a
              href="https://docs.google.com/spreadsheets/d/1wLDboZZBfBwMKswMYcRIXxz6DxRZJyAa6KPW6TvR-dM/edit?usp=sharing">Hexflower
              Template</a></p>
          <p>Example Game: <a
              href="https://storysynth.org/Hexflower/1wLDboZZBfBwMKswMYcRIXxz6DxRZJyAa6KPW6TvR-dM/">Hexflower Demo</a>
          </p>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col">
        <div class="card card-body shadow">
          <h3>Phases</h3>
          <img src="/img/phases-thumbnail.png" class="rounded mx-auto d-block format-thumbnail"
            alt="thumbnail of the phases format">
          <p>In this format, players draw a series of cards from different decks. The Phases format is different from
            Shuffled in that cards are drawn from different decks in a rotation (e.g. A, B, C, A, B, C).</p>
          <p>Template: <a
              href="https://docs.google.com/spreadsheets/d/1HataDfV2lrA4hfzmLgDjXH09dEMLQV6OT10tVH9G52A/edit?usp=sharing">Phases
              Template</a></p>
          <p>Example Game: <a href="https://storysynth.org/Games/Around-The-Realm">Around the Realm</a></p>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col">
        <div class="card card-body shadow">
          <h3>Secrets</h3>
          <img src="/img/secrets-thumbnail.png" class="rounded mx-auto d-block format-thumbnail"
            alt="thumbnail of the secrets format">
          <p>In this format, players select roles and then receive prompts over a series of round. In each round,
            players will recive one public prompt (seen by all) and one secret prompt (unique to their role).</p>
          <p>Template: <a
              href="https://docs.google.com/spreadsheets/d/1JwMF02DSxNKtjHp6u-wyznSs-iEG_3DpOobgc17I16o/edit?usp=sharing">Secrets
              Template</a></p>
          <p>Example Game: <a
              href="https://storysynth.org/SecretCards/1JNpjWl4apfOrvRgmKVUXMTy4n7FoITxAbu_zVLhhVmI/">The July
              Crisis</a></p>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col">
        <div class="card card-body shadow">
          <h3>Slot Machine</h3>
          <img src="/img/slot-thumbnail.png" class="rounded mx-auto d-block format-thumbnail"
            alt="thumbnail of the secrets format">
          <p>In this format, players draw cards that have been randomly assembled from a series of lists. It is similar
            to the Generator format except that the randomly selected prompts appear on a card and that all the cards
            are assembled at the start of a session.</p>
          <p>Template: <a
              href="https://docs.google.com/spreadsheets/d/1t5LRUQG9DzMJ3kd8E9DZV7_EbE8J5-Gqhz7TWQ4Y-uU/edit?usp=sharing">Slot
              Machine Template</a></p>
          <p>Example Game: <a href="https://storysynth.org/Games/Post-Work-Paradise/">Post-Work Paradise</a></p>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col">
        <div class="card card-body shadow">
          <h3>Monster</h3>
          <img src="/img/monster-thumbnail.png" class="rounded mx-auto d-block format-thumbnail"
            alt="thumbnail of the monster format">
          <p>In this format, players go through a non-random series of card prompts. Some cards give a player the option
            to select among multiple different options which start collapsed (mostly hidden) but can be expanded for
            them.
          </p>
          <p>Template: <a
              href="https://docs.google.com/spreadsheets/d/1NgNHy7Qe1R8KhGR2cOmJwL2aOl2tocBemW2HIAKjrvI/edit?usp=sharing">Monster
              Template</a></p>
          <p>Example Game: <a href="https://monster.diegeticgames.com/#/">Dawn of the Monster Invasion</a></p>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col">
        <div class="card card-body shadow">
          <h3>Timed</h3>
          <img src="/img/timed-thumbnail.png" class="rounded mx-auto d-block format-thumbnail"
            alt="thumbnail of the timed format">
          <p>In this format, players select roles and then start a shared timer. Then, private messages appear for each
            player at pre-determined timestamps.</p>
          <p>Inspired by <a href="https://www.patreon.com/posts/find-fix-finish-35788333">Find / Fix / Finish</a></p>
          <p>Template: <a
              href="https://docs.google.com/spreadsheets/d/1yq2AKwaYL1uZrCnEfwgSpC0SPkQAZqnCdjNxH_pm018/edit?usp=sharing">Timed
              Template</a></p>
          <p>Example Game: <a href="https://storysynth.org/Timed/1yq2AKwaYL1uZrCnEfwgSpC0SPkQAZqnCdjNxH_pm018/">Timed
              Demo</a></p>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col text-left">
        <div class="card card-body shadow">
          <h2>Extensions</h2>
          <p>Story Synth has a variety of extensions that add functionality to any of the formats. Here's a quick
            overview of the different extensions. Check out the <a
              href="https://docs.storysynth.org/guide/extensions.html">Extensions Page</a> of the documentation to learn
            more. </p>
          <ul>
            <li>Static Box: shows a simple text box that is always visable</li>
            <li>Player Turn Order: lets players track turn order</li>
            <li>Plus-Minus: shows a state tracking system where players can increase or decrease the values of things
              they are tracking</li>
            <li>Editable Lists: creates a list that players can edit, adding and deleting items</li>
            <li>Dice Roller: lets players can roll dice by typing commands like '2d6'</li>
            <li>Standard Deck: provides a way of drawing from a deck of playing cards</li>
            <li>Journal Entries: allows players to write journal entries</li>
            <li>Hexflower as Extension: embeds a hexflower in a different game format</li>
            <li>Generator as Extension: embeds a Generator in a different game format</li>
          </ul>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col text-left">
        <h2>Custom Options</h2>
        <div class="card card-body shadow">
          <p>There are many ways to further customize your game, including visual styles, images, metadata, and more.
            Check out the <a href="https://docs.storysynth.org/guide/options.html">Options Page</a> in the
            documentations to learn more.</p>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
export default {
  name: "app-formatsAndExtensions",
  data: function () {
    return {

    };
  },
  metaInfo() {
    return {
      title: "Story Synth Formats and Extensions",
      meta: [
        {
          property: "description",
          content: "An overview of Story Synth formats and extensions",
          vmid: "description",
        },
        {
          property: "og:title",
          content: "Story Synth Formats and Extensions",
          vmid: "og:title",
        },
        {
          property: "og:description",
          content: "An overview of Story Synth formats and extensions",
          vmid: "og:description",
        },
        {
          property: "og:image",
          content: "https://diegeticgames.com/uploads/gallery-collage-unfurl.png",
          vmid: "og:image",
        },
        {
          property: "og:url",
          content: "https://storysynth.org/" + this.$route.fullPath,
          vmid: "og:url",
        },
        {
          property: "twitter:card",
          content: "summary",
          vmid: "twitter:card",
        },
        {
          property: "og:site_name",
          content: "Story Synth",
          vmid: "og:site_name",
        },
        {
          property: "twitter:image:alt",
          content: "The logos of several games in the Story Synth Gallery",
          vmid: "twitter:image:alt",
        },
      ],
    }
  },
  mounted() {
    let body = document.getElementById("app"); // document.body;
    body.classList.remove(body.classList[0]);
    body.classList.add("style-template-homepage");

    document.dispatchEvent(new Event("x-app-rendered"));
    if (location.hostname.toString() !== 'localhost') {
      this.$mixpanel.track('Visited Format Overview');
    }
  },
};
</script>

<style scoped>
.container {
  max-width: 720px;
}

h1,
h2 {
  text-align: center;
  margin-top: .8rem;
  margin-bottom: 1rem;
}

li {
  list-style-position: inside;
}

p,
li {
  font-size: 18px;
}

.card {
  margin-bottom: 1.5rem;
}

.format-thumbnail {
  max-width: 100% !important;
  margin-bottom: 15px;
}
</style>
